/**
 * Accessible hero carousel for homepage.
 *
 * The slide images are decorative (the meaningful content is the text overlay,
 * which is already in the accessibility tree), so they stay aria-hidden — but
 * the carousel controls (arrows, indicators, play/pause) are real, labeled,
 * keyboard-operable controls, and autoplay respects prefers-reduced-motion and
 * pauses on hover/focus so it never fights a reader for attention.
 */
(function () {
  function initHeroCarousel() {
    const root = document.querySelector(".hero-carousel");
    if (!root) return;

    const track = root.querySelector(".hero-slides");
    const slides = root.querySelectorAll(".hero-slide");
    const prev = root.querySelector(".hero-arrow.prev");
    const next = root.querySelector(".hero-arrow.next");
    const dotsWrap = root.querySelector(".hero-dots");
    const playToggle = root.querySelector(".hero-play-toggle");
    if (slides.length < 2) {
      dotsWrap?.remove();
      playToggle?.remove();
      prev?.remove();
      next?.remove();
      return;
    }

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let index = 0;
    let timer = null;
    let userPaused = reduceMotion; // reduced-motion users start paused; they can opt in
    let hovering = false;
    let focused = false;

    const dots = [];

    function loadSlideBg(slide) {
      if (!slide || slide.dataset.bgLoaded) return;
      const src = slide.dataset.bg;
      if (!src) return;
      const webp = src.replace(/\.jpe?g$/i, '.webp');
      // Three sequential assignments, weakest first: a browser that can't
      // parse a later value simply keeps whichever earlier one it understood.
      slide.style.backgroundImage = `url('${src}')`;
      slide.style.backgroundImage = `-webkit-image-set(url('${webp}') 1x)`;
      slide.style.backgroundImage = `image-set(url('${webp}') type('image/webp'), url('${src}') type('image/jpeg'))`;
      slide.dataset.bgLoaded = 'true';
    }

    function prefetchAdjacent(i) {
      loadSlideBg(slides[i]);
      loadSlideBg(slides[(i + 1) % slides.length]);
    }

    if (dotsWrap) {
      slides.forEach((_, i) => {
        const dot = document.createElement("button");
        dot.type = "button";
        dot.className = "hero-dot";
        dot.setAttribute("aria-label", `Go to slide ${i + 1} of ${slides.length}`);
        dot.addEventListener("click", () => {
          show(i);
          restartAuto();
        });
        dotsWrap.appendChild(dot);
        dots.push(dot);
      });
    }

    function show(i) {
      slides.forEach((s, n) => s.classList.toggle("active", n === i));
      dots.forEach((d, n) => {
        const isActive = n === i;
        d.classList.toggle("active", isActive);
        d.setAttribute("aria-current", isActive ? "true" : "false");
      });
      index = i;
      prefetchAdjacent(i);
    }

    function nextSlide() {
      show((index + 1) % slides.length);
    }

    function prevSlide() {
      show((index - 1 + slides.length) % slides.length);
    }

    function isPlaying() {
      return !userPaused && !hovering && !focused;
    }

    function updatePlayToggle() {
      if (!playToggle) return;
      const playing = isPlaying();
      playToggle.setAttribute("aria-pressed", userPaused ? "false" : "true");
      playToggle.setAttribute("aria-label", userPaused ? "Play slideshow" : "Pause slideshow");
      playToggle.classList.toggle("is-paused", !playing);
    }

    function startAuto() {
      clearInterval(timer);
      timer = null;
      if (!isPlaying()) {
        updatePlayToggle();
        return;
      }
      timer = setInterval(nextSlide, 6000);
      updatePlayToggle();
    }

    function restartAuto() {
      startAuto();
    }

    prev?.addEventListener("click", () => {
      prevSlide();
      restartAuto();
    });
    next?.addEventListener("click", () => {
      nextSlide();
      restartAuto();
    });

    playToggle?.addEventListener("click", () => {
      userPaused = !userPaused;
      restartAuto();
    });

    root.addEventListener("mouseenter", () => {
      hovering = true;
      startAuto();
    });
    root.addEventListener("mouseleave", () => {
      hovering = false;
      startAuto();
    });
    root.addEventListener("focusin", () => {
      focused = true;
      startAuto();
    });
    root.addEventListener("focusout", (e) => {
      // Only treat as "left the carousel" once focus moves outside it entirely.
      if (root.contains(e.relatedTarget)) return;
      focused = false;
      startAuto();
    });

    track?.setAttribute("aria-hidden", "true");
    track?.setAttribute("role", "presentation");
    root.setAttribute("role", "region");
    root.setAttribute("aria-roledescription", "carousel");

    show(0);
    startAuto();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initHeroCarousel);
  } else {
    initHeroCarousel();
  }
})();

export {};
